package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminSettingsWhatsAppUpsert
import org.gymly.theme.AdminColors
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.rememberPasswordVisible
import org.gymly.util.userMessage

@Composable
internal fun SettingsWhatsAppPanel(compact: Boolean) {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var running by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }

    var enabled by remember { mutableStateOf(false) }
    var apiBaseUrl by remember { mutableStateOf("https://graph.facebook.com") }
    var apiVersion by remember { mutableStateOf("v21.0") }
    var phoneNumberId by remember { mutableStateOf("") }
    var accessToken by remember { mutableStateOf("") }
    var accessTokenConfigured by remember { mutableStateOf(false) }
    var templateName by remember { mutableStateOf("membership_weekly_reminder") }
    var templateLanguage by remember { mutableStateOf("es") }
    var templateWelcome by remember { mutableStateOf("client_welcome") }
    var templatePaymentReceived by remember { mutableStateOf("payment_received") }
    var templatePaymentPending by remember { mutableStateOf("payment_pending") }
    var templateMembershipExpiring by remember { mutableStateOf("membership_expiring") }
    var templateMarketing by remember { mutableStateOf("") }
    var templateTermsNotice by remember { mutableStateOf("") }
    var templateTermsNoticeApproved by remember { mutableStateOf(false) }
    var sendHour by remember { mutableStateOf("9") }
    var saturdayEnabled by remember { mutableStateOf(true) }
    var sundayEnabled by remember { mutableStateOf(true) }
    var simulateOnly by remember { mutableStateOf(true) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            success = null
            try {
                val cfg = AdminRepo.api.getSettingsWhatsApp()
                enabled = cfg.enabled
                apiBaseUrl = cfg.apiBaseUrl
                apiVersion = cfg.apiVersion
                phoneNumberId = cfg.phoneNumberId.orEmpty()
                accessToken = ""
                accessTokenConfigured = cfg.accessTokenConfigured
                templateName = cfg.templateName
                templateLanguage = cfg.templateLanguage
                templateWelcome = cfg.templateWelcome
                templatePaymentReceived = cfg.templatePaymentReceived
                templatePaymentPending = cfg.templatePaymentPending
                templateMembershipExpiring = cfg.templateMembershipExpiring
                templateMarketing = cfg.templateMarketing.orEmpty()
                templateTermsNotice = cfg.templateTermsNotice.orEmpty()
                templateTermsNoticeApproved = cfg.templateTermsNoticeApproved
                sendHour = cfg.sendHour.toString()
                saturdayEnabled = cfg.saturdayEnabled
                sundayEnabled = cfg.sundayEnabled
                simulateOnly = cfg.simulateOnly
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cargar WhatsApp.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    fun buildUpsert(): AdminSettingsWhatsAppUpsert? {
        val hour = sendHour.trim().toIntOrNull()
        if (hour == null || hour !in 0..23) {
            error = "La hora de envío debe estar entre 0 y 23."
            return null
        }
        if (enabled && !simulateOnly && phoneNumberId.isBlank()) {
            error = "Phone Number ID es obligatorio si WhatsApp está activo y no es simulación."
            return null
        }
        if (enabled && !simulateOnly && !accessTokenConfigured && accessToken.isBlank()) {
            error = "Access token es obligatorio la primera vez (o deja simulación activada)."
            return null
        }
        return AdminSettingsWhatsAppUpsert(
            enabled = enabled,
            apiBaseUrl = apiBaseUrl.trim().ifBlank { "https://graph.facebook.com" },
            apiVersion = apiVersion.trim().ifBlank { "v21.0" },
            phoneNumberId = phoneNumberId.trim().ifBlank { null },
            accessToken = accessToken.trim().ifBlank { null },
            templateName = templateName.trim().ifBlank { "membership_weekly_reminder" },
            templateLanguage = templateLanguage.trim().ifBlank { "es" },
            templateWelcome = templateWelcome.trim().ifBlank { "client_welcome" },
            templatePaymentReceived = templatePaymentReceived.trim().ifBlank { "payment_received" },
            templatePaymentPending = templatePaymentPending.trim().ifBlank { "payment_pending" },
            templateMembershipExpiring = templateMembershipExpiring.trim().ifBlank { "membership_expiring" },
            templateMarketing = templateMarketing.trim().ifBlank { null },
            templateTermsNotice = templateTermsNotice.trim().ifBlank { null },
            templateTermsNoticeApproved = templateTermsNoticeApproved && templateTermsNotice.isNotBlank(),
            sendHour = hour,
            saturdayEnabled = saturdayEnabled,
            sundayEnabled = sundayEnabled,
            simulateOnly = simulateOnly,
        )
    }

    VerticalScrollColumn(
        Modifier
            .fillMaxSize()
            .widthIn(max = if (compact) 720.dp else 880.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Text("WhatsApp", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
        Text(
            "Con WhatsApp habilitado se avisa al socio (si tiene teléfono) en: bienvenida, pago confirmado, " +
                "pago pendiente y membresía por vencer (1 día / según agenda). " +
                "Además: recordatorio sábados/domingos solo planes WEEKLY (informativo; no crea órdenes).",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
        Text(
            "Las plantillas deben estar aprobadas en Meta Business Manager. " +
                "El texto del correo se edita en Configuración → Notificaciones.",
            color = AdminColors.Primary,
            fontSize = 12.sp,
            fontWeight = FontWeight.Medium,
        )

        when {
            loading -> {
                Spacer(Modifier.height(24.dp))
                CircularProgressIndicator(color = AdminColors.Primary, modifier = Modifier.align(Alignment.CenterHorizontally))
            }
            else -> {
                Column(
                    Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(12.dp))
                        .background(AdminColors.SurfaceContainer)
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    CheckRow("Habilitar WhatsApp", enabled) { enabled = it }
                    CheckRow("Enviar los sábados (WEEKLY)", saturdayEnabled) { saturdayEnabled = it }
                    CheckRow("Enviar los domingos (WEEKLY)", sundayEnabled) { sundayEnabled = it }
                    CheckRow(
                        "Solo simular (no llama a Meta; útil en desarrollo)",
                        simulateOnly,
                    ) { simulateOnly = it }

                    Field("API base URL", apiBaseUrl) { apiBaseUrl = it }
                    Field("API version", apiVersion) { apiVersion = it }
                    Field("Phone Number ID", phoneNumberId) { phoneNumberId = it }
                    Field(
                        if (accessTokenConfigured) "Access token (dejar vacío para no cambiar)" else "Access token",
                        accessToken,
                        secret = true,
                    ) { accessToken = it }
                    Field("Idioma plantillas (ej. es)", templateLanguage) { templateLanguage = it }
                    Field("Hora recordatorio WEEKLY (0-23, América/Caracas)", sendHour) { sendHour = it }

                    Text("Plantillas Meta por evento", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 14.sp)
                    Field("Bienvenida (alta de socio)", templateWelcome) { templateWelcome = it }
                    Field("Pago confirmado / nota de entrega", templatePaymentReceived) { templatePaymentReceived = it }
                    Field("Pago pendiente de confirmación", templatePaymentPending) { templatePaymentPending = it }
                    Field("Membresía por vencer", templateMembershipExpiring) { templateMembershipExpiring = it }
                    Field("Recordatorio semanal WEEKLY", templateName) { templateName = it }
                    Field("Promociones por WhatsApp (plantilla con cabecera de imagen)", templateMarketing) { templateMarketing = it }
                    Field("Plantilla de aviso de términos (variable {{1}} = nombre del socio)", templateTermsNotice) { templateTermsNotice = it; templateTermsNoticeApproved = false }
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Checkbox(
                            checked = templateTermsNoticeApproved,
                            onCheckedChange = { templateTermsNoticeApproved = it },
                            enabled = templateTermsNotice.isNotBlank(),
                        )
                        Text("Aprobada en Meta (la confirma quien administra WhatsApp)", fontSize = 13.sp)
                    }
                    val estadoAviso = when {
                        templateTermsNotice.isBlank() -> "Sin configurar: no se pueden enviar avisos de términos."
                        !templateTermsNoticeApproved -> "Pendiente de aprobación: no se enviará hasta marcarla como aprobada."
                        else -> "Lista para enviar avisos de términos (si WhatsApp está habilitado)."
                    }
                    Text(estadoAviso, color = AdminColors.TextMuted, fontSize = 12.sp)

                    Text(
                        "Variables sugeridas en Meta:\n" +
                            "• Bienvenida: {{1}} nombre · {{2}} empresa\n" +
                            "• Pago / pendiente: {{1}} nombre · {{2}} monto · {{3}} método · {{4}} fecha\n" +
                            "• Por vencer: {{1}} nombre · {{2}} plan · {{3}} vence · {{4}} días\n" +
                            "• WEEKLY: {{1}} nombre · {{2}} plan · {{3}} monto · {{4}} periodo",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }

                error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp) }
                success?.let { Text(it, color = AdminColors.Primary, fontSize = 13.sp) }

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Button(
                        onClick = {
                            val upsert = buildUpsert() ?: return@Button
                            scope.launch {
                                saving = true
                                error = null
                                success = null
                                try {
                                    val saved = AdminRepo.api.upsertSettingsWhatsApp(upsert)
                                    accessToken = ""
                                    accessTokenConfigured = saved.accessTokenConfigured
                                    success = "Configuración de WhatsApp guardada."
                                } catch (e: Exception) {
                                    error = e.userMessage("No se pudo guardar.")
                                } finally {
                                    saving = false
                                }
                            }
                        },
                        enabled = !saving && !running,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(if (saving) "Guardando…" else "Guardar")
                    }
                    OutlinedButton(
                        onClick = {
                            scope.launch {
                                running = true
                                error = null
                                success = null
                                try {
                                    val result = AdminRepo.api.runWhatsAppWeeklyRemindersNow()
                                    success = "Aviso informativo enviado/simulado a ${result.sentCount} socio(s). " +
                                        "No se crearon órdenes ni cobros."
                                } catch (e: Exception) {
                                    error = e.userMessage("No se pudo ejecutar el recordatorio.")
                                } finally {
                                    running = false
                                }
                            }
                        },
                        enabled = !saving && !running,
                    ) {
                        Text(if (running) "Ejecutando…" else "Probar ahora")
                    }
                    OutlinedButton(onClick = { reload() }, enabled = !saving && !running) {
                        Text("Recargar")
                    }
                }
            }
        }
    }
}

@Composable
private fun CheckRow(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Checkbox(
            checked = checked,
            onCheckedChange = onChange,
            colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
        )
        Text(label, color = AdminColors.Text, fontSize = 13.sp)
    }
}

@Composable
private fun Field(label: String, value: String, secret: Boolean = false, onChange: (String) -> Unit) {
    Column {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(4.dp))
        val (visible, toggleVisible) = rememberPasswordVisible()
        OutlinedTextField(
            value = value,
            onValueChange = onChange,
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            visualTransformation = if (secret && !visible) PasswordVisualTransformation() else VisualTransformation.None,
            trailingIcon = if (secret) {
                { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
            } else {
                null
            },
            shape = RoundedCornerShape(10.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = AdminColors.Primary,
                unfocusedBorderColor = AdminColors.Outline,
            ),
        )
    }
}
